import React, { useEffect, useRef } from "react";
import { formatUsd } from "@veyso/core";
import { useAccount } from "../auth/Gate.js";
import { Spinner } from "../components/ui.js";
import type { LanguagesView, LaunchVersionView } from "../api.js";
import { LanguageSwitch } from "../editor/LanguageSwitch.js";
import { launchVideoUrl } from "../languages/languages.js";
import { ago } from "./studio.js";

/**
 * The player, and the versions it can play.
 *
 * Choosing a card only changes what plays and what Download saves; a new
 * version is always made from the newest one.
 */
export const VersionStrip: React.FC<{
  versions: LaunchVersionView[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Opens the languages of the selected version; the button shows only on that card. */
  onLanguages?: () => void;
  /** Whether that panel is open, so the button says so. */
  languagesOpen?: boolean;
  /** The selected version's languages, for the switch above the player. */
  languages?: LanguagesView | null;
  /** The language the player shows; null is the English. */
  languageCode?: string | null;
  onSwitchLanguage?: (code: string | null) => void;
}> = ({
  versions,
  selectedId,
  onSelect,
  onLanguages,
  languagesOpen = false,
  languages = null,
  languageCode = null,
  onSwitchLanguage,
}) => {
  const account = useAccount();
  const shown = versions.filter((version) => version.status === "ready" || version.status === "making");
  const selected = shown.find((version) => version.id === selectedId && version.status === "ready") ?? null;

  const language = languageCode ? (languages?.languages.find((one) => one.code === languageCode) ?? null) : null;
  const src = selected?.videoUrl ? launchVideoUrl(selected.videoUrl, language) : null;

  // Switching language swaps the source; the moment and whether it was
  // playing are carried over to the new film once it can be seeked.
  const player = useRef<HTMLVideoElement | null>(null);
  const resume = useRef<{ time: number; playing: boolean } | null>(null);
  useEffect(() => {
    resume.current = null;
  }, [selected?.id]);
  const switchTo = (code: string | null) => {
    const video = player.current;
    const target = code ? (languages?.languages.find((one) => one.code === code) ?? null) : null;
    // No new film, no new load to resume after.
    const changes = selected?.videoUrl ? launchVideoUrl(selected.videoUrl, target) !== src : false;
    if (video && changes) resume.current = { time: video.currentTime, playing: !video.paused };
    onSwitchLanguage?.(code);
  };
  const restore = (video: HTMLVideoElement) => {
    const at = resume.current;
    if (!at) return;
    resume.current = null;
    video.currentTime = at.time;
    if (at.playing) void video.play().catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-4">
      {selected && onSwitchLanguage ? (
        <div className="flex flex-wrap items-center gap-2">
          <LanguageSwitch
            view={languages}
            current={language?.code ?? null}
            onSwitch={switchTo}
            email={account?.email ?? null}
          />
          {onLanguages ? (
            // Where a language is added, beside where one is watched.
            <button
              type="button"
              className="btn btn-ghost btn-sm border border-dashed border-[var(--edge-strong)] text-ember-600"
              aria-expanded={languagesOpen}
              onClick={onLanguages}
            >
              + Language
            </button>
          ) : null}
        </div>
      ) : null}
      {src ? (
        <video
          // A new version, not a seek: without the key the old film's position
          // and buffer carry over into the next one. A new language keeps both.
          key={selected?.id}
          ref={player}
          className="aspect-video w-full rounded-[14px] border border-[var(--edge-strong)] bg-black"
          src={src}
          onLoadedMetadata={(event) => restore(event.currentTarget)}
          poster={selected?.thumbnailUrl ?? undefined}
          controls
          preload="metadata"
          // Not autoplay: this is somebody's own video and they get to start it.
          playsInline
        />
      ) : null}

      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-[14px] font-semibold text-ink-100">Versions</h2>
          <span className="text-[12px] text-ink-300">Last 5 kept · a sixth removes the oldest</span>
        </div>
        <ul
          // Focusable so the row scrolls from the keyboard as well as by touch.
          tabIndex={0}
          aria-label="Versions"
          className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto pb-1.5 focus-visible:outline-2 focus-visible:outline-[var(--edge-focus)]"
        >
          {shown.map((version) => {
            const current = version.id === selected?.id;
            const making = version.status === "making";
            return (
              <li
                key={version.id}
                className={`flex flex-[0_0_168px] snap-start flex-col gap-1.5 rounded-[10px] bg-bay-850 p-2 ${
                  current ? "border-2 border-ember-500" : "border border-[var(--edge)]"
                }`}
              >
                <button
                  type="button"
                  className="flex flex-col gap-1.5 rounded-[6px] text-left focus-visible:outline-2 focus-visible:outline-[var(--edge-focus)] disabled:cursor-default"
                  disabled={making}
                  aria-pressed={current}
                  aria-label={making ? `v${version.number}, being made` : `Play v${version.number}`}
                  onClick={() => onSelect(version.id)}
                >
                  <div className="relative aspect-video overflow-hidden rounded-[6px] bg-bay-800">
                    {version.thumbnailUrl ? (
                      <img src={version.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                    ) : null}
                    {making ? (
                      <div className="absolute inset-0 flex items-center justify-center gap-2 text-[11.5px] text-ink-300">
                        <Spinner />
                        Making…
                      </div>
                    ) : null}
                    {current ? (
                      <span className="absolute top-1.5 left-1.5 rounded-[4px] bg-ink-100 px-1.5 py-0.5 text-[10px] font-semibold text-bay-850">
                        Playing
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-baseline justify-between gap-1.5">
                    <span className="text-[13px] font-semibold text-ink-100">v{version.number}</span>
                    <span className="tnum text-[11px] text-ink-300">
                      {making ? "" : version.priceUsd > 0 ? formatUsd(version.priceUsd) : "Free"}
                    </span>
                  </div>
                  <div className="line-clamp-2 min-h-[31px] text-[11.5px] leading-[1.35] text-ink-200">
                    {version.request}
                  </div>
                </button>
                <div className="flex items-center justify-between gap-1.5">
                  <span className="text-[11px] text-ink-400">{ago(version.createdAt)}</span>
                  {version.downloadUrl && !making ? (
                    <a
                      className="btn btn-secondary btn-sm"
                      href={version.downloadUrl}
                      aria-label={`Download v${version.number}`}
                    >
                      Download
                    </a>
                  ) : null}
                </div>
                {current && onLanguages ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm w-full"
                    aria-expanded={languagesOpen}
                    aria-label={`Languages for v${version.number}`}
                    onClick={onLanguages}
                  >
                    Languages
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
